import {
  Box,
  Breadcrumbs,
  Button,
  Container,
  Grid,
  Link,
  Popover,
  Typography,
} from "@material-ui/core";
import { differenceInDays, format, subDays } from "date-fns";
import { useEffect, useState } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Helmet } from "react-helmet-async";
import { Link as RouterLink } from "react-router-dom";
import { BoxedLoader } from "../../components/BoxedLoader";
import PredictedTrends from "../../components/dashboard/charts/PredictedTrends";
import PredictionGeneralOverview from "../../components/dashboard/prediction/PredictionGeneralOverview";
import useSettings from "../../hooks/useSettings";
import ChevronDownIcon from "../../icons/ChevronDown";
import ChevronRightIcon from "../../icons/ChevronRight";
import { GET_TRIP_ANALYTICS } from "../../queries/prediction/tripAnalyticsQuery";
import { graphQLApiService } from "../../services/GraphQLApiService";
import { hasPermission } from "../../utils/handlePermissions";
import { useTranslation } from "react-i18next";
import { pl } from "date-fns/locale";

const PredictionOverview = () => {
  const { t, i18n } = useTranslation();
  const date = new Date();
  const { settings } = useSettings();
  const [isLoading, setIsLoading] = useState(true);

  const [minimumBasePrice, setMinimumBasePrice] = useState(0);
  const [maximumBasePrice, setMaximumBasePrice] = useState(0);
  const [averageBasePrice, setAverageBasePrice] = useState(0);

  const [countriesNames, setCountriesNames] = useState([]);
  const [countriesStats, setCountriesStats] = useState({
    min: [],
    max: [],
    avg: [],
  });

  const [departureNames, setDepartureNames] = useState([]);
  const [departureStats, setDepartureStats] = useState({
    min: [],
    max: [],
    avg: [],
  });

  const [mealNames, setMealNames] = useState([]);
  const [mealStats, setMealStats] = useState({
    min: [],
    max: [],
    avg: [],
  });
  const [propertyCategoryNames, setPropertyCategoryNames] = useState([]);
  const [propertyCategoryStats, setPropertyCategoryStats] = useState({
    min: [],
    max: [],
    avg: [],
  });
  const [transportTypeNames, setTransportTypeNames] = useState([]);
  const [transportTypeStats, setTransportTypeStats] = useState({
    min: [],
    max: [],
    avg: [],
  });
  const [tripDurationNames, setTripDurationNames] = useState([]);
  const [tripDurationStats, setTripDurationStats] = useState({
    min: [],
    max: [],
    avg: [],
  });

  const [selectionRange, setState] = useState([
    {
      startDate: subDays(new Date("2023-07-31"), 30),
      endDate: new Date("2023-07-31"),
      key: "selection",
    },
  ]);

  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    if (
      format(selectionRange[0].startDate, "yyyy-MM-dd") ===
      format(selectionRange[0].endDate, "yyyy-MM-dd")
    ) {
      return;
    }
    setIsLoading(true);
    graphQLApiService
      .query(GET_TRIP_ANALYTICS, {
        startDate: format(selectionRange[0].startDate, "yyyy-MM-dd"),
        endDate: format(selectionRange[0].endDate, "yyyy-MM-dd"),
      })
      .then((data) => {
        setMinimumBasePrice(data.data.tripAnalytics.minimumBasePrice);
        setMaximumBasePrice(data.data.tripAnalytics.maximumBasePrice);
        setAverageBasePrice(data.data.tripAnalytics.averageBasePrice);
        let stats = { min: [], max: [], avg: [] };
        let names = [];
        data.data.tripAnalytics.statsByCountry.forEach((item) => {
          names.push(item.countryCode);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setCountriesStats(stats);
        setCountriesNames(names);

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByDepartureFrom.forEach((item) => {
          names.push(`${item.departureCity} ${item.departureAirportCode}`);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setDepartureStats(stats);
        setDepartureNames(names);

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByMeal.forEach((item) => {
          names.push(item.mealCode);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setMealStats(stats);
        setMealNames(names);

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByPropertyCategory.forEach((item) => {
          names.push(item.propertyCategory);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setPropertyCategoryStats(stats);
        setPropertyCategoryNames(names);

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByTransportType.forEach((item) => {
          names.push(item.transportType);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setTransportTypeStats(stats);
        setTransportTypeNames(names);

        stats = { min: [], max: [], avg: [] };
        names = [];
        data.data.tripAnalytics.statsByTripDuration.forEach((item) => {
          names.push(item.tripDuration);
          stats.min.push(item.minimumBasePrice);
          stats.max.push(item.maximumBasePrice);
          stats.avg.push(item.averageBasePrice);
        });
        setTripDurationStats(stats);
        setTripDurationNames(names);
        setIsLoading(false);
      });
  }, [selectionRange]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  return (
    <>
      {hasPermission("dashboard_analytics_overview") && (
        <>
          <Helmet>
            <title>Dashboard: {t("title.Overview")} | Qtravel Search</title>
          </Helmet>
          <Box
            sx={{
              backgroundColor: "background.default",
              minHeight: "100%",
              py: 4,
            }}
          >
            <Container maxWidth={settings.compact ? "xl" : false}>
              <Grid
                container
                alignItems="end"
                justifyContent="space-between"
                spacing={3}
              >
                <Grid item>
                  <Typography color="textPrimary" variant="h5">
                    {t("title.Overview")}
                  </Typography>
                  <Breadcrumbs
                    aria-label="breadcrumb"
                    separator={<ChevronRightIcon fontSize="small" />}
                    sx={{ mt: 1 }}
                  >
                    <Link
                      color="textPrimary"
                      component={RouterLink}
                      to="/dashboard/main"
                      variant="subtitle2"
                    >
                      Dashboard
                    </Link>
                    <Typography color="textSecondary" variant="subtitle2">
                      {t("title.Overview")}
                    </Typography>
                  </Breadcrumbs>
                </Grid>
                <Grid item>
                  <Button
                    aria-describedby={id}
                    variant="contained"
                    onClick={handleClick}
                    endIcon={<ChevronDownIcon fontSize="small" />}
                  >
                    {t("pages.prediction.analyze_range")}{" "}
                    {differenceInDays(
                      selectionRange[0].endDate,
                      selectionRange[0].startDate
                    ) + 1}{" "}
                    {t(`main.days`)}{" "}
                    {selectionRange && selectionRange[0] && (
                      <>
                        ({format(selectionRange[0].startDate, "dd-MM-yyyy")} -{" "}
                        {format(selectionRange[0].endDate, "dd-MM-yyyy")})
                      </>
                    )}
                  </Button>
                  <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: "bottom",
                      horizontal: "left",
                    }}
                  >
                    <DateRangePicker
                      locale={i18n.language != "pl" ? undefined : pl}
                      staticRanges={[
                        {
                          label: t("other.today"),
                          hasCustomRendering: false,
                          range: () => ({
                            startDate: date,
                            endDate: date,
                          }),
                          isSelected() {
                            return false;
                          },
                        },
                        {
                          label: t("other.last_week"),
                          hasCustomRendering: false,
                          range: () => ({
                            startDate: subDays(
                              date,
                              ((date.getDay() + 7) % 7) + 6
                            ),
                            endDate: subDays(date, (date.getDay() + 7) % 7),
                          }),
                          isSelected() {
                            return false;
                          },
                        },
                        {
                          label: t("other.this_month"),
                          hasCustomRendering: false,
                          range: () => ({
                            startDate: new Date(
                              date.getFullYear(),
                              date.getMonth(),
                              1
                            ),
                            endDate: new Date(
                              date.getFullYear(),
                              date.getMonth() + 1,
                              0
                            ),
                          }),
                          isSelected: () => false,
                        },
                        {
                          label: t("other.last_month"),
                          hasCustomRendering: false,
                          range: () => ({
                            startDate: new Date(
                              date.getFullYear(),
                              date.getMonth() - 1,
                              1
                            ),
                            endDate: new Date(
                              date.getFullYear(),
                              date.getMonth(),
                              0
                            ),
                          }),
                          isSelected() {
                            return false;
                          },
                        },
                      ]}
                      months={2}
                      direction="horizontal"
                      ranges={selectionRange}
                      showSelectionPreview={true}
                      moveRangeOnFirstSelection={false}
                      onChange={(item) => setState([item.selection])}
                    />
                  </Popover>
                </Grid>
              </Grid>
              <Box sx={{ pt: 3 }}>
                <PredictionGeneralOverview
                  averagePricePPPerStay={averageBasePrice || "-"}
                  minimalPricePPPerStay={minimumBasePrice || "-"}
                  maximalPricePPPerStay={maximumBasePrice || "-"}
                />
              </Box>
              <Box sx={{ backgroundColor: "background.default", pt: 3 }}>
                {isLoading && <BoxedLoader height={"445px"} />}
                {!isLoading && (
                  <PredictedTrends
                    categories={countriesNames}
                    data={countriesStats}
                    title={t(`other.countries`)}
                    tooltipInfo={t(`other.tooltip_Destination_country_1`)}
                  />
                )}
              </Box>
              <Box sx={{ backgroundColor: "background.default", pt: 3 }}>
                {isLoading && <BoxedLoader height={"445px"} />}
                {!isLoading && (
                  <PredictedTrends
                    categories={departureNames}
                    data={departureStats}
                    title={t(`main.departurecity`)}
                    tooltipInfo={t(`other.tooltip_Destination_price`)}
                  />
                )}
              </Box>
              <Grid container spacing={2} py={3}>
                <Grid item md={6} sm={6} xs={12}>
                  {isLoading && <BoxedLoader height={"445px"} />}
                  {!isLoading && (
                    <PredictedTrends
                      categories={mealNames}
                      data={mealStats}
                      title={t(`main.departurecity`)}
                      tooltipInfo={`other.tooltip_overview_1`}
                    />
                  )}
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  {isLoading && <BoxedLoader height={"445px"} />}
                  {!isLoading && (
                    <PredictedTrends
                      categories={propertyCategoryNames}
                      data={propertyCategoryStats}
                      title={t(`other.property_category`)}
                      tooltipInfo={"Property category"}
                    />
                  )}
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  {isLoading && <BoxedLoader height={"445px"} />}
                  {!isLoading && (
                    <PredictedTrends
                      categories={transportTypeNames}
                      data={transportTypeStats}
                      title={t(`main.transporttype`)}
                      tooltipInfo={t(`other.tooltip_Destination_country_3`)}
                    />
                  )}
                </Grid>
                <Grid item md={6} sm={6} xs={12}>
                  {isLoading && <BoxedLoader height={"445px"} />}
                  {!isLoading && (
                    <PredictedTrends
                      categories={tripDurationNames}
                      data={tripDurationStats}
                      title={t(`other.trip_durations`)}
                      tooltipInfo={t(`other.tooltip_overview_2`)}
                    />
                  )}
                </Grid>
              </Grid>
            </Container>
          </Box>
        </>
      )}
    </>
  );
};

export default PredictionOverview;
